import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { Steps } from 'antd';
import YousignSetupFeeStep from './YousignSetupFeeStep';
import YousignBusinessInfoStep from './YousignBusinessInfoStep';
import YousignCreatePreviewStep from './YousignCreatePreviewStep';
import YousignPreviewStep from './YousignPreviewStep';
import YousignSignContractStep from './YousignSignContractStep';
import LoadingIndicator from '../../LoadingIndicator';
import useForm from '../../../hooks/utility/useForm';
import useApiClient from '../../../hooks/api/useApiClient';
import useBusinessProfile from '../../../hooks/context/useBusinessProfile';
import { BillingMode, SignatureProvider } from '../../../services/exports/Constants';
import { persistedContractDiscount } from '../../../services/helpers/ContractDiscountHelpers';

/**
 * Ordered keys for the master (Yousign) contract wizard.
 */
export const ContractStepKey = Object.freeze({
  SetupFee: 'setup_fee',
  BusinessInfo: 'business_info',
  CreatePreview: 'create_preview',
  Preview: 'preview',
  Sign: 'sign',
});

/**
 * Master (Yousign) contract flow, split into 4 wizard steps — one component per
 * step (setup fee → create preview → preview → sign). This orchestrator owns the
 * shared form data, the current position, and the signature link; each step owns
 * its own logic.
 *
 * The recipient details are NOT persisted while previewing (the preview endpoint
 * generates the PDF from the passed params without saving them); they are only
 * committed on init-sign. Because that state lives only in memory, the whole
 * flow is one wizard here rather than separate onboarding steps.
 */
export default function YousignContractFlow({ contract, setContract, onSuccess, isSkippable, skip, skipping }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:YousignContractFlow' });
  const { ServiceContractsManager } = useApiClient();
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { getValue } = useBusinessProfile();

  const setupPackages = serviceProvider.setup_packages;
  const hasSetupFee = useMemo(
    () =>
      collect(setupPackages)
        .filter((item) => item.stripe_billing_price !== 0 || item.internal_invoice_billing_price !== 0)
        .isNotEmpty(),
    [setupPackages],
  );

  // The setup-fee step is dropped when there is no setup fee to charge.
  const steps = useMemo(
    () => [
      ...(hasSetupFee ? [{ key: ContractStepKey.SetupFee, title: t('steps.setup_fee') }] : []),
      { key: ContractStepKey.BusinessInfo, title: t('steps.business_info') },
      { key: ContractStepKey.CreatePreview, title: t('steps.create_preview') },
      { key: ContractStepKey.Preview, title: t('steps.preview') },
      { key: ContractStepKey.Sign, title: t('steps.sign') },
    ],
    [hasSetupFee, t],
  );

  // The stored percentage as the backend bills it, never capped to today's grant range.
  const effectiveDiscount = persistedContractDiscount(contract?.contract_discount_percentage);

  const [currentStepKey, setCurrentStepKey] = useState(null);
  const [signatureLink, setSignatureLink] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const { data, update } = useForm({
    store_quantity: contract?.store_quantity ?? 1,
    billing_mode: contract?.billing_mode ?? BillingMode.Stripe,
    setup_package_id: contract?.setup_package_id ?? setupPackages[0]?.id,
    // The signer is usually the owner, whose details Get Started takes before the contract: a
    // contract without a recipient yet starts from them, and they stay editable (a GmbH's
    // managing director may sign instead). A recipient the contract already holds wins.
    signer_first_name: contract?.signer_first_name || getValue('owner_first_name') || '',
    signer_last_name: contract?.signer_last_name || getValue('owner_last_name') || '',
    recipient_email: contract?.recipient_email || getValue('owner_email') || '',
    additional_locations: contract?.additional_locations ?? [],
    special_agreements: contract?.special_agreements ?? '',
  });

  const isRecipientComplete = (_data) =>
    Boolean(_data.signer_first_name?.trim()) &&
    Boolean(_data.signer_last_name?.trim()) &&
    Boolean(_data.recipient_email?.trim());

  // Determine where to resume from using persisted signals only: an active
  // signature request → sign step; a package still to pick → setup-fee step;
  // otherwise the recipient/create-preview step.
  useEffect(() => {
    if (!contract?.id) {
      return;
    }

    let cancelled = false;

    ServiceContractsManager.getSignatureLink(contract.id)
      .then((response) => {
        if (cancelled) {
          return;
        }

        if (response?.success && response.data) {
          setSignatureLink(response.data);
          setCurrentStepKey(ContractStepKey.Sign);

          return;
        }

        if (hasSetupFee && !contract.setup_package_id) {
          setCurrentStepKey(ContractStepKey.SetupFee);

          return;
        }

        setCurrentStepKey(isRecipientComplete(contract) ? ContractStepKey.Preview : ContractStepKey.BusinessInfo);
      })
      .finally(() => {
        if (!cancelled) {
          setInitializing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [contract?.id]);

  const currentStepIndex = useMemo(
    () =>
      Math.max(
        0,
        steps.findIndex((step) => step.key === currentStepKey),
      ),
    [steps, currentStepKey],
  );

  const cancelSignature = () => {
    setSignatureLink(null);
    setCurrentStepKey(ContractStepKey.Preview);
  };

  const onContractCreated = (link) => {
    setSignatureLink(link);
    setCurrentStepKey(ContractStepKey.Sign);
  };

  const recipientComplete =
    Boolean(data.signer_first_name?.trim()) &&
    Boolean(data.signer_last_name?.trim()) &&
    Boolean(data.recipient_email?.trim());

  // A step is only clickable once it's valid to jump to: create-preview needs a
  // setup fee, preview needs the recipient details filled, sign needs an
  // existing signature request. The setup-fee step is always reachable.
  const isStepClickable = (step) => {
    switch (step.key) {
      case ContractStepKey.Preview:
        return recipientComplete;
      case ContractStepKey.Sign:
        return Boolean(signatureLink);
      default:
        return true;
    }
  };

  const onStepChange = (index) => {
    const target = steps[index];

    if (!target || !isStepClickable(target)) {
      return;
    }

    setCurrentStepKey(target.key);
  };

  function renderStep() {
    switch (currentStepKey) {
      case ContractStepKey.SetupFee:
        return (
          <YousignSetupFeeStep
            data={data}
            update={update}
            setContract={setContract}
            setupPackages={setupPackages}
            discount={effectiveDiscount}
            onSelected={() => setCurrentStepKey(ContractStepKey.BusinessInfo)}
          />
        );
      case ContractStepKey.BusinessInfo:
        return (
          <YousignBusinessInfoStep
            contract={contract}
            hasSetupFee={hasSetupFee}
            setContract={setContract}
            onBack={() => setCurrentStepKey(ContractStepKey.SetupFee)}
            onNext={() => setCurrentStepKey(ContractStepKey.CreatePreview)}
          />
        );
      case ContractStepKey.CreatePreview:
        return (
          <YousignCreatePreviewStep
            data={data}
            update={update}
            isSkippable={isSkippable}
            skip={skip}
            skipping={skipping}
            onBack={() => setCurrentStepKey(ContractStepKey.BusinessInfo)}
            onNext={() => setCurrentStepKey(ContractStepKey.Preview)}
          />
        );
      case ContractStepKey.Preview:
        return (
          <YousignPreviewStep
            contract={contract}
            data={{
              signer_first_name: data.signer_first_name,
              signer_last_name: data.signer_last_name,
              recipient_email: data.recipient_email,
              additional_locations: data.additional_locations,
              special_agreements: data.special_agreements,
              signature_provider: SignatureProvider.Yousign,
            }}
            update={update}
            onBack={() => setCurrentStepKey(ContractStepKey.CreatePreview)}
            onCreated={onContractCreated}
          />
        );
      case ContractStepKey.Sign:
        return (
          <YousignSignContractStep
            contractId={contract?.id}
            signatureLink={signatureLink}
            onSuccess={onSuccess}
            onCancel={cancelSignature}
          />
        );
      default:
        return null;
    }
  }

  if (initializing || !currentStepKey) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <Steps
        current={currentStepIndex}
        onChange={onStepChange}
        items={steps.map((step) => ({
          title: step.title,
          disabled: !isStepClickable(step),
        }))}
        className="mb-lg"
        responsive
      />
      {renderStep()}
    </div>
  );
}
